import { computeHoldings } from '../../domain/holdings';
import { suggestRisk } from '../../domain/kyc/riskRating';
import { DEMO_TODAY } from '../../lib/format';
import { createRandom } from '../../lib/random';
import type { RegistryState } from '../types';
import { buildInvestorLogins, generateHolders, PERSONA_LOGIN_ID } from './seedHolders';
import { generateLedger } from './seedLedger';
import {
  ATO_REPORTS,
  buildAudit,
  buildDocuments,
  buildTasks,
  buildTaxBatches,
  MIGRATIONS,
} from './seedRecords';
import { CLASSES, FIDES_TENANT_ID, FUNDS, TENANTS } from './seedReference';
import { JASON_USER_ID, USERS } from './seedUsers';
import { FIDES_SETUP_DEFAULTS, OPPORTUNITIES, SEED_OFFERS } from './seedSetup';
import {
  buildRaiseApplications,
  RAISE_BANK_LINES,
  RAISE_CLASS,
  RAISE_FUND,
  RAISE_OFFER,
} from './seedRaise';
import { buildApplications } from './seedApplications';
import { buildPlatformEntities } from './seedEntities';
import { buildBankLines, buildKycCases } from './seedCompliance';
import {
  buildCommunications,
  buildExceptionTasks,
  buildLifecycleDocuments,
  buildPaymentExceptions,
  buildRedemptionRuns,
  REMITTANCES,
} from './seedLifecycleRecords';
import { buildApprovals, buildChangeRequests } from './seedRequests';

export const STATE_VERSION = 18;

const RISK_REVIEW_DEMO_HOLDER = 'h-f008';

export function createSeedState(): RegistryState {
  const random = createRandom(20261016);
  const holders = generateHolders(random);
  const investorLogins = buildInvestorLogins(holders);
  const { ledger, commitments, calls, distributions, capitalReturns, pastRedemptions } =
    generateLedger(random, holders);
  const paymentExceptions = buildPaymentExceptions(holders, distributions);
  const redemption = buildRedemptionRuns(
    pastRedemptions,
    ledger,
    holders,
    buildChangeRequests(holders),
  );
  const call = calls.find((item) => item.id === 'cc-opp-3')!;
  const kycCases = buildKycCases(holders);
  const valueByHolder = new Map<string, number>();
  for (const holding of computeHoldings(ledger, CLASSES, commitments, DEMO_TODAY))
    valueByHolder.set(holding.holderId, (valueByHolder.get(holding.holderId) ?? 0) + holding.value);
  const ratedHolders = holders.map((holder) =>
    holder.id === RISK_REVIEW_DEMO_HOLDER
      ? holder
      : {
          ...holder,
          riskRating: suggestRisk(holder, kycCases, valueByHolder.get(holder.id) ?? 0).rating,
        },
  );
  const holdersWithUnits = (fundId: string) =>
    new Set(ledger.filter((entry) => entry.fundId === fundId).map((entry) => entry.holderId)).size;

  const taxBatches = buildTaxBatches().map((batch) => ({
    ...batch,
    statementCount: batch.status === 'PUBLISHED' ? holdersWithUnits(batch.fundId) : 0,
  }));
  const fidesHolderCount = holders.filter((holder) => holder.tenantId === FIDES_TENANT_ID).length;
  const atoReports = ATO_REPORTS.map((report) => {
    if (report.id === 'ato-aiir-26') return { ...report, records: fidesHolderCount };
    if (report.id === 'ato-tfn-q1') {
      return {
        ...report,
        records: holders.filter((holder) => holder.tfnStatus === 'QUOTED').length,
      };
    }
    return report;
  });
  const migrations = MIGRATIONS.map((job) =>
    job.status === 'COMMITTED'
      ? { ...job, rows: holdersWithUnits(job.fundId), matched: holdersWithUnits(job.fundId) }
      : job,
  );

  const applications = [...buildApplications(), ...buildRaiseApplications()];
  const funds = [...FUNDS, RAISE_FUND];
  return {
    version: STATE_VERSION,
    tenants: TENANTS,
    currentTenantId: FIDES_TENANT_ID,
    currentUserId: JASON_USER_ID,
    currentInvestorLoginId: PERSONA_LOGIN_ID,
    users: USERS,
    funds,
    classes: [...CLASSES, RAISE_CLASS],
    holders: ratedHolders,
    platformEntities: buildPlatformEntities(ratedHolders, applications, funds),
    commitments,
    ledger,
    calls,
    distributions,
    applications,
    kycCases,
    bankLines: [...RAISE_BANK_LINES, ...buildBankLines(call, holders)],
    approvals: buildApprovals(holders),
    changeRequests: redemption.changeRequests,
    taxBatches,
    atoReports,
    tasks: [...buildTasks(holders, call), ...buildExceptionTasks(paymentExceptions)],
    documents: [
      ...buildLifecycleDocuments(capitalReturns),
      ...buildDocuments(holders, distributions, call),
    ],
    audit: buildAudit(ledger),
    investorLogins,
    migrations,
    capitalReturns,
    redemptionRuns: redemption.runs,
    paymentExceptions,
    remittances: REMITTANCES,
    communications: buildCommunications(distributions, calls, capitalReturns, holders),
    opportunities: OPPORTUNITIES,
    fundSetups: [],
    offers: [...SEED_OFFERS, RAISE_OFFER],
    issueRuns: [],
    reportRuns: [],
    houseDefaults: { [FIDES_TENANT_ID]: { ...FIDES_SETUP_DEFAULTS } },
  };
}
